import { useFormik } from "formik";
import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { object, string } from "yup";
import { messageApi } from "../../services/AuthServices";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { motion } from "framer-motion";

function MainSection() {
  const [isDisable, setisDisable] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [recaptchaScale, setRecaptchaScale] = useState(1);
  const recaptchaRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    // Handle reCAPTCHA responsive scaling
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 380) {
        setRecaptchaScale(0.85);
      } else if (width < 480) {
        setRecaptchaScale(0.95);
      } else {
        setRecaptchaScale(1);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const AdminSchema = object().shape({
    email: string()
      .required("Please enter email.")
      .email("Invalid Email Format. Please try again.")
      .trim("Please enter email."),

    firstName: string()
      .required("Please enter first name.")
      .trim("Please enter first name."),
    lastName: string()
      .required("Please enter last name.")
      .trim("Please enter last name."),
    message: string()
      .required("Please enter Message.")
      .trim("Please enter Message."),
  });

  const submitClicked = async (data) => {
    if (!recaptchaError && !recaptchaToken) {
      toast("Please complete the reCAPTCHA verification", { type: "error" });
      return;
    }

    setisDisable(true);

    await messageApi(data)
      .then((response) => {
        setisDisable(false);
        resetForm();
        setRecaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }

        toast("Message Send Successfully", { type: "success" });
        navigate("/");
      })
      .catch((error) => {
        setisDisable(false);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setRecaptchaToken(null);

        if (error?.response?.status == 422)
          toast(error?.response?.data?.message, { type: "error" });
        else if (error?.response?.status == 500)
          toast(error.response.data.message, { type: "error" });
        else
          toast(error?.response?.data?.message || "Something went wrong", {
            type: "error",
          });
      });
  };

  const {
    handleSubmit,
    resetForm,
    handleBlur,
    handleChange,
    values,
    errors,
    touched,
  } = useFormik({
    validationSchema: AdminSchema,

    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
      message: "",
      mobile: "",
    },
    onSubmit: async () => {
      let formdata = new FormData();
      formdata.append("first_name", values.firstName);
      formdata.append("last_name", values.lastName);
      formdata.append("email", values.email);
      formdata.append("mobile", values.mobile);
      formdata.append("message", values.message);
      formdata.append("recaptcha_token", recaptchaToken);
      submitClicked(formdata);
    },

    onReset: () => {},
  });

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  const handleRecaptchaError = () => {
    setRecaptchaError(true);
  };

  return (
    <>
      {/* Page Header */}
      <motion.div
        className="static-page"
        style={{ paddingBottom: 0 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container">
          <div className="static-page-header">
            <div className="static-page-title-wrapper">
              <div className="static-page-icon">
                <i className="fas fa-envelope"></i>
              </div>
              <div className="static-page-title-text">
                <h1>Contact Us</h1>
                <p>We'd love to hear from you</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Map Section */}
      <div className="binduz-er-map-area">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <motion.div
                className="binduz-er-map-box"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <iframe
                  title="map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3380379.500621907!2d-111.9307474!3d34.16803495!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x872b08ebcb4c186b%3A0x423927b17fc1cd71!2sArizona%2C%20USA!5e0!3m2!1sen!2sin!4v1721936276727!5m2!1sen!2sin"
                  width="100%"
                  height="400"
                  style={{ border: "0" }}
                  allowFullScreen=""
                  loading="lazy"
                  aria-hidden="false"
                ></iframe>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="binduz-er-contact-us-area">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <motion.div
                className="binduz-er-contact-us-box"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <form onSubmit={((e) => e?.preventDefault(), handleSubmit)}>
                  <div className="binduz-er-contact-title">
                    <h4 className="binduz-er-title">
                      GET IN TOUCH & LET US KNOW
                    </h4>
                  </div>
                  <div className="row">
                    <div className=" col-lg-6">
                      <div className="binduz-er-input-box">
                        <input
                          type="text"
                          placeholder="Enter First Name*"
                          name="firstName"
                          id="firstName"
                          value={values.firstName}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          style={{
                            borderColor:
                              touched?.firstName && errors?.firstName && "red",
                          }}
                        />
                        <i className="fal fa-user"></i>
                      </div>
                      {touched?.firstName && errors?.firstName && (
                        <span className="text-danger">{errors.firstName}</span>
                      )}
                    </div>
                    <div className=" col-lg-6">
                      <div className="binduz-er-input-box">
                        <input
                          type="text"
                          placeholder="Enter Last Name*"
                          name="lastName"
                          id="lastName"
                          value={values.lastName}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          style={{
                            borderColor:
                              touched?.lastName && errors?.lastName && "red",
                          }}
                        />
                        <i className="fal fa-user"></i>
                      </div>
                      {touched?.lastName && errors?.lastName && (
                        <span className="text-danger">{errors.lastName}</span>
                      )}
                    </div>

                    <div className=" col-lg-6">
                      <div className="binduz-er-input-box">
                        <input
                          type="email"
                          placeholder="Enter Email*"
                          name="email"
                          id="email"
                          value={values.email}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          style={{
                            borderColor:
                              touched?.email && errors?.email && "red",
                          }}
                        />
                        <i className="fal fa-envelope"></i>
                      </div>
                      {touched?.email && errors?.email && (
                        <span className="text-danger">{errors.email}</span>
                      )}
                    </div>
                    <div className=" col-lg-6">
                      <div className="binduz-er-input-box">
                        <input
                          type="number"
                          placeholder="Enter Mobile Number"
                          name="mobile"
                          id="mobile"
                          value={values.mobile}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                        <i className="fal fa-phone"></i>
                      </div>
                    </div>
                    {/* <div className=" col-lg-4">
                      <div className="binduz-er-input-box binduz-er-select-item">
                        <DropDown
                          datas={category1}
                          selected={selectedCategory}
                          action={changeCategory1}
                          className="mexuvo-binduz-tech-dropdown"
                        />
                      </div>
                    </div> */}
                    <div className=" col-lg-12">
                      <div className="binduz-er-input-box">
                        <textarea
                          cols="30"
                          rows="10"
                          placeholder="Enter Message*"
                          name="message"
                          id="message"
                          value={values.message}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          style={{
                            marginBottom:
                              !touched?.message && !errors?.message && "22px",
                            borderColor:
                              touched?.message && errors?.message && "red",
                          }}
                        ></textarea>
                        <i className="fal fa-pencil"></i>
                      </div>
                      {touched?.message && errors?.message && (
                        <span className="text-danger">{errors.message}</span>
                      )}{" "}
                    </div>
                    <div className="col-lg-12">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          marginTop: "20px",
                          marginBottom: "20px",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            transform: `scale(${recaptchaScale})`,
                            transformOrigin: "center",
                            maxWidth: "100%",
                            overflow: "visible",
                          }}
                        >
                          {!recaptchaError && (
                            <ReCAPTCHA
                              ref={recaptchaRef}
                              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                              onChange={handleRecaptchaChange}
                              onExpired={handleRecaptchaExpired}
                              onErrored={handleRecaptchaError}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={isDisable}
                    onClick={handleSubmit}
                    className="binduz-er-main-btn contact-us-box-button"
                  >
                    {isDisable ? (
                      <>
                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane" style={{ marginRight: '8px' }}></i>
                        Submit Request
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            </div>
          </div>
          {/* <div className="row pt-90">
            <div className=" col-lg-4">
              <div className="binduz-er-contact-info-box">
                <h3 className="binduz-er-title">Corporate Office</h3>
                <ul>
                  <li>
                    Address: <span>Lorem 142 Str., 2352, Pro State, USA</span>
                  </li>
                  <li>
                    Phone: <span>+33-257634 534</span>
                  </li>
                  <li>
                    Email: <span>info@webexample.com</span>
                  </li>
                </ul>
                <a className="binduz-er-main-btn" href="#">
                  Make A Call
                </a>
              </div>
            </div>
            <div className=" col-lg-4">
              <div className="binduz-er-contact-info-box">
                <h3 className="binduz-er-title">Main HQ</h3>
                <ul>
                  <li>
                    Address: <span>Lorem 142 Str., 2352, Pro State, USA</span>
                  </li>
                  <li>
                    Phone: <span>+33-257634 534</span>
                  </li>
                  <li>
                    Email: <span>info@webexample.com</span>
                  </li>
                </ul>
                <a className="binduz-er-main-btn active" href="#">
                  Make A Call
                </a>
              </div>
            </div>
            <div className=" col-lg-4">
              <div className="binduz-er-contact-info-box">
                <h3 className="binduz-er-title">Advertise</h3>
                <ul>
                  <li>
                    Address: <span>Lorem 142 Str., 2352, Pro State, USA</span>
                  </li>
                  <li>
                    Phone: <span>+33-257634 534</span>
                  </li>
                  <li>
                    Email: <span>info@webexample.com</span>
                  </li>
                </ul>
                <a className="binduz-er-main-btn" href="#">
                  Make A Call
                </a>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </>
  );
}

export default MainSection;
